import { useMemo } from 'react'

import type { GenSourceItem } from '~/constants/generationSource'
import {
  GENERATION_SOURCE_LIST,
  buildGenerationSourceList,
} from '~/constants/generationSource'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'

/**
 * 同時同量 (発電) の発電形式 9 区分を、**個人設定の永続値の順・色**で返す hook
 * (AC-SPS-007 / AC-SPS-008: 並び替え・色変更を「同時同量(発電)画面の凡例・
 * 積み上げ順に即時反映する")。
 *
 * `~/constants/generationSource` の `buildGenerationSourceList` が
 * 「`StackOrderItem[]` を渡すだけで済む」形で用意されており、本 hook がその
 * 実配線 (issue #7058 / 旧コメントの「永続値の実配線は本 module の担当外 —
 * 別 issue」) を担う。
 *
 * - 設定未取得 (`undefined`) / 空配列のときは既定順・既定色
 *   (`GENERATION_SOURCE_LIST`) を返す。既定値は `DEFAULT_STACK_ORDER_ITEMS` と
 *   同一なので、**未カスタマイズのユーザーには見た目の変化が無い**。
 * - 欠落・重複・未知の区分の扱いは `buildGenerationSourceList` に委ねる
 *   (常に 9 区分を返すので、積み上げ合計が実績合計と一致する
 *   doji-hatsu.md AC-BDH-010 / AC-BDH-020 NOTE を破らない)。
 * - `throwOnError: false` は `useBusinessAreaFilter` と同じ理由: 設定の取得
 *   失敗で画面全体を落とさず、既定値で描き続ける。凡例と積み上げが**同じ配列**
 *   から導かれている限り、凡例が嘘になることはない。
 *
 * 参照は `useGetPersonalSettingsQuery` 経由なので、同じ query key を読む他の
 * 呼び出し (業務エリア等) と react-query のキャッシュを共有する。個人設定側の
 * mutation が楽観更新でキャッシュを書き換えると、本 hook の購読者も同じ
 * レンダリングで追随する (= 設定モーダルを開いたままチャートが変わる)。
 */
export function useGenerationSourceList(): readonly GenSourceItem[] {
  const { data } = useGetPersonalSettingsQuery({ throwOnError: false })
  const stackOrder = data?.settings?.stackOrder

  return useMemo(() => {
    if (stackOrder === undefined || stackOrder.length === 0) {
      return GENERATION_SOURCE_LIST
    }
    return buildGenerationSourceList(stackOrder)
  }, [stackOrder])
}
